import { all, call, put, takeEvery } from 'redux-saga/effects';

import * as actions from '@actions/taxCodes';
import { fetchEntityJurisdictions } from '@api/entityJurisdictions';
import {
  getTaxCodes,
  getTaxCodesRates,
  getTaxCodesTransactionTypes,
  saveClientTaxCodes,
} from '@api/taxCodes';
import types from '@constants/action-types';
import { history, taxCodesRoutes } from '@routes';
import { toastError, toastSuccess } from '@store/notifications';

export const handleGetTaxCodesJurisdictionsRequest = function* ({ payload }) {
  try {
    const response = yield call(fetchEntityJurisdictions, payload);
    if (response.isAxiosError)
      return yield put(actions.getTaxCodesJurisdictionsFailure(response.message));
    yield put(actions.getTaxCodesJurisdictionsSuccess(response));
  } catch (e) {
    yield put(actions.getTaxCodesJurisdictionsFailure(e));
  }
};

export const handleGetTaxCodesRequest = function* ({ payload }) {
  try {
    const response = yield call(getTaxCodes, payload);
    if (response.isAxiosError) return yield put(actions.getTaxCodesFailure(response.message));
    yield put(actions.getTaxCodesSuccess(response));
  } catch (e) {
    yield put(actions.getTaxCodesFailure(e));
  }
};

export const handleSaveClientTaxCodesRequest = function* ({ payload }) {
  try {
    const response = yield call(saveClientTaxCodes, payload);

    yield put([
      actions.saveClientTaxCodesSuccess(response),
      toastSuccess({
        message: {
          id: 'pages.tax-codes.notifications.saved',
          defaultMessage: 'Saved successfully.',
        },
      }),
    ]);
    if (payload?.onSuccess) yield call(payload.onSuccess);
  } catch (errors) {
    const { nonFieldErrors, fieldErrors } = errors?.data || {};

    yield put([
      toastError({
        message: nonFieldErrors ||
          (fieldErrors && Object.values(fieldErrors)?.[0]) || {
            id: 'global.notification.api-error-default',
            defaultMessage: 'An unexpected error occurred, please try again.',
          },
      }),
      actions.getTaxCodesFailure(errors),
    ]);
  }
};

export const handleGetTaxCodesRatesRequest = function* ({ payload }) {
  try {
    const response = yield call(getTaxCodesRates, payload);
    if (response.isAxiosError) return yield put(actions.getTaxCodesRatesFailure(response.message));
    yield put(actions.getTaxCodesRatesSuccess(response));
  } catch (e) {
    yield put(actions.getTaxCodesRatesFailure(e));
  }
};

export const handleGetClientTransactionTypesRequest = function* ({ payload }) {
  try {
    const response = yield call(getTaxCodesTransactionTypes, payload);

    yield put(actions.getClientTransactionTypesSuccess(response));

    yield call(
      history.push,
      taxCodesRoutes.DETAILS_CREDIT.make(
        payload.entityJurisdictionId,
        encodeURIComponent(payload.entityName),
        true,
        1
      )
    );
  } catch (errors) {
    const { nonFieldErrors, fieldErrors } = errors?.data || {};

    yield put([
      toastError({
        message: nonFieldErrors ||
          (fieldErrors && Object.values(fieldErrors)?.[0]) || {
            id: 'global.notification.api-error-default',
            defaultMessage: 'An unexpected error occurred, please try again.',
          },
      }),
      actions.getClientTransactionTypesFailure(errors),
    ]);
  }
};

export const handleGetContinuousClientTransactionTypesRequest = function* ({ payload }) {
  try {
    const response = yield call(getTaxCodesTransactionTypes, { entityJurisdictionId: payload });
    if (response.isAxiosError)
      return yield put(actions.getContinuousClientTransactionTypesFailure(response.message));
    yield put(
      actions.getContinuousClientTransactionTypesSuccess({ results: response.results, id: payload })
    );
  } catch (e) {
    yield put(actions.getContinuousClientTransactionTypesFailure(e));
  }
};

export const handleGetTaxCodesWithTransactionTypesRequest = function* ({ payload }) {
  try {
    const response = yield call(fetchEntityJurisdictions, payload);

    if (response.isAxiosError)
      return yield put(actions.getTaxCodesWithTransactionTypesFailure(response.message));
    yield put(actions.getTaxCodesWithTransactionTypesSuccess(response));
  } catch (e) {
    yield put(actions.getTaxCodesWithTransactionTypesFailure(e));
  }
};

export default all([
  takeEvery(types.SAVE_CLIENT_TAX_CODES, handleSaveClientTaxCodesRequest),
  takeEvery(types.GET_TAX_CODES_JURISDICTIONS_REQUEST, handleGetTaxCodesJurisdictionsRequest),
  takeEvery(types.GET_TAX_CODES_REQUEST, handleGetTaxCodesRequest),
  takeEvery(types.GET_TAX_CODES_RATES_REQUEST, handleGetTaxCodesRatesRequest),
  takeEvery(types.GET_CLIENT_TRANSACTION_TYPES_REQUEST, handleGetClientTransactionTypesRequest),
  takeEvery(
    types.GET_TAX_CODES_WITH_TRANSACTION_TYPES_REQUEST,
    handleGetTaxCodesWithTransactionTypesRequest
  ),
  takeEvery(
    types.GET_CONTINUOUS_CLIENT_TRANSACTION_TYPES_REQUEST,
    handleGetContinuousClientTransactionTypesRequest
  ),
]);
